import type { FetchError } from 'ofetch';
import type { BackendError } from '~/lib/shop-api';
import { infoFlyout, openFlyout } from '~/composables/flyout';
import ErrorParser from '~/lib/error-parser';
import { getPrice } from '@i22team/rocket';
import { trackFBEvent } from '~/lib/tracking-fb';
import { useCartStore } from '~/stores/cart';
import serializeCartAction from '~/lib/tealium/serializers/cart';
import { tealiumLink } from '~/lib/tealium';
import { cleanArticleNumber } from '~/lib/clean-input';

type ItemNameCallback = (articleNumber: string) => string;

const FALLBACK_MESSAGE = 'Hoppla! Da ist etwas schiefgelaufen. Bitte versuche es noch einmal.';

export const isAdding = ref(false);

export type ItemFromOffer = {
  articleNumber: string;
  name: string;
  slug?: string;
  price?: number | undefined;
  wasPrice?: number | undefined;
  category?: string | undefined;
  quantity?: number;
};

type SimpleOffer = {
  articleNumber: string;
  name: string;
  quantity?: number;
};

export type AddToCartItem = Item | ItemFromOffer | SimpleOffer;

export const getAddToCartItemFromStore = (
  articleNumber: string,
  cartItems: Item[]
): AddToCartItem | undefined => {
  if (cartItems && cartItems.length > 0) {
    const item = cartItems.find((e: any) => e.articleNumber === articleNumber);
    if (item) return item;
  }
  return undefined;
};

export const getAddToCartItemFromOffer = (
  offer: ListOffer | Offer | OfferProductAlternative | ListOfferProductAlternative
): AddToCartItem | undefined => {
  if (!offer.product || !offer.product.articleNumber) return undefined;
  const family = 'family' in offer.product ? offer.product.family : undefined;
  return {
    articleNumber: offer.product.articleNumber,
    price: getPrice(offer.prices || [], 'DEFAULT', 'ONETIME')?.amount || undefined,
    wasPrice: getPrice(offer.prices || [], 'WAS', 'ONETIME')?.amount || undefined,
    slug: offer.product.slug || '',
    name: offer.product.name || '',
    category: family?.primaryCategory?.name || undefined,
  };
};

export const getProductAddToCartErrorMessage = (
  articleNumbers: string[],
  errorType: string,
  itemNameCallback?: ItemNameCallback
) => {
  if (articleNumbers.length === 0) return undefined;

  const names = articleNumbers.map((articleNumber) =>
    itemNameCallback ? itemNameCallback(cleanArticleNumber(articleNumber)) : articleNumber
  );

  const namesAsHTML = `<strong>${names.join('</strong>, <strong>')}</strong>`;
  switch (errorType) {
    case 'taken':
      return `Folgende Produkte befanden sich bereits im Warenkorb und wurden daher nicht erneut hinzugefügt:<br>${namesAsHTML}`;
    case 'not_found':
      return `Folgende Produkte wurden nicht gefunden und daher nicht zum Warenkorb hinzugefügt:<br>${namesAsHTML}`;
    default:
      return undefined;
  }
};

export const handleCartErrors = async (
  error: FetchError<BackendError> | Error,
  itemNameCallback?: ItemNameCallback,
  fallbackMessage = FALLBACK_MESSAGE
) => {
  // Show Dialog by normal errors (caused by JS).
  // @ts-ignore
  if (!error?.data && error?.status !== 404) {
    infoFlyout(fallbackMessage, { type: 'error' });
    return;
  }

  // @ts-ignore
  let errors = (error.data?.errors || {}) as BackendError;

  // 404 means sold out
  // @ts-ignore
  if (error?.status === 404) {
    errors = { cart_validations: [{ error: ['not_available'] }] };
  }

  // Initializing error buckets
  const articleNumbersNotFound: string[] = [];
  const articleNumbersTaken: string[] = [];
  // @ts-ignore
  const others: BackendError = [];

  // Iterate through the keys in the errors object
  Object.keys(errors).forEach((errorType) => {
    // Iterate through the array of errors for the current key
    errors[errorType]?.forEach((errorEntry) => {
      if (!errorEntry.value) {
        if (!others[errorType]) others[errorType] = [];
        others[errorType].push(errorEntry);
        return;
      }
      // Group errors based on their type
      switch (errorEntry.error) {
        case 'not_found':
          articleNumbersNotFound.push(errorEntry.value as string);
          break;
        case 'taken':
          articleNumbersTaken.push(errorEntry.value as string);
          break;
        default:
          if (!others[errorType]) others[errorType] = [];
          others[errorType].push(errorEntry);
          break;
      }
    });
  });

  const messagesToShow = [
    getProductAddToCartErrorMessage(articleNumbersTaken, 'taken', itemNameCallback),
    getProductAddToCartErrorMessage(articleNumbersNotFound, 'not_found', itemNameCallback),
  ].filter(Boolean);

  if (Object.keys(others).length > 0) {
    messagesToShow.push(...ErrorParser.getMessagesFlatten(others, fallbackMessage));
  }

  if (messagesToShow.length === 0) {
    messagesToShow.push(FALLBACK_MESSAGE);
  }
  infoFlyout(messagesToShow.join('<br><br>'), { htmlSafe: true, type: 'warning' });
};

export const addedOffersCallback = (items: AddToCartItem[]) => {
  openFlyout({
    component: {
      is: markRaw(
        defineAsyncComponent(() => import('~/components/add-to-cart-dialog-content.vue'))
      ),
      props: { items },
    },
    success: true,
  });
};

export const addToCart = async (
  offers: (
    ListOffer | Offer | SimpleOffer | OfferProductAlternative | ListOfferProductAlternative
  )[],
  callback?: (items: AddToCartItem[]) => void
) => {
  if (isAdding.value) {
    return false;
  }
  const items: AddToCartItem[] = offers
    .map((offer) => {
      if ('product' in offer) {
        return getAddToCartItemFromOffer(offer);
      }
      if ('articleNumber' in offer && offer.articleNumber) {
        return {
          articleNumber: offer.articleNumber,
          name: offer.name || '',
          quantity: offer.quantity || 1,
        };
      }
      return false;
    })
    .filter(Boolean);

  if (items.length === 0) return false;

  const addToCartItems = items.map((item) => ({
    articleNumber: item.articleNumber,
    quantity: 'quantity' in item ? item.quantity || 1 : 1,
  }));
  try {
    const cartStore = useCartStore();
    isAdding.value = true;
    await cartStore.addCartItems(addToCartItems);

    if (callback) {
      callback(items);
    } else {
      addedOffersCallback(items);
    }

    tealiumLink({
      wt_link_id: 'content.button.in-den-warenkorb',
      event_name: 'cart_add',
      ...serializeCartAction(undefined, items),
    });

    const contentIds: string[] = [];
    let price = 0;
    const name: string[] = [];
    items.forEach((item) => {
      contentIds.push(item.articleNumber);
      name.push(item.name);
      if ('price' in item && item.price !== undefined) {
        price += item.price;
      }
    });

    trackFBEvent('AddToCart', {
      content_ids: contentIds,
      content_name: name.join(', '),
      content_type: 'product',
      currency: 'EUR',
      value: price / 100,
      num_of_items: items.length,
    });

    return true;
  } catch (error: Error | any) {
    await handleCartErrors(
      error,
      (articleNumber) =>
        items.find((item) => item.articleNumber === articleNumber)?.name || articleNumber
    );
  } finally {
    isAdding.value = false;
  }
  return false;
};
